import { useEffect, useState } from 'react';

const isClient = typeof window !== 'undefined';

/**
 * Returns `true` once the component has mounted on the client. Use this to
 * guard code that touches `window` / `document` so SSR / first paint is safe.
 */
export const useIsMounted = (): boolean => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
};

/**
 * Module-level registry of `MediaQueryList` listeners, keyed by query string.
 *
 * Each unique query has exactly one underlying `MediaQueryList` subscription
 * that fans out to all `useMediaQuery` consumers. When the last consumer
 * unsubscribes, we tear down the listener so the registry doesn't leak.
 */
type MediaQueryEntry = {
  mql: MediaQueryList;
  onChange: (e: MediaQueryListEvent) => void;
  listeners: Set<(matches: boolean) => void>;
  currentMatches: boolean;
};

const mediaQueryRegistry: Map<string, MediaQueryEntry> = new Map();

const subscribe = (query: string, listener: (matches: boolean) => void): (() => void) => {
  if (!isClient) return () => undefined;

  let entry = mediaQueryRegistry.get(query);
  if (!entry) {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => {
      const existing = mediaQueryRegistry.get(query);
      if (!existing) return;
      existing.currentMatches = e.matches;
      existing.listeners.forEach((l) => l(e.matches));
    };
    mql.addEventListener('change', onChange);
    entry = { mql, onChange, listeners: new Set(), currentMatches: mql.matches };
    mediaQueryRegistry.set(query, entry);
  }

  entry.listeners.add(listener);

  return () => {
    const current = mediaQueryRegistry.get(query);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size === 0) {
      // No more consumers for this query — detach the shared listener.
      current.mql.removeEventListener('change', current.onChange);
      mediaQueryRegistry.delete(query);
    }
  };
};

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    if (!isClient) return false;
    return mediaQueryRegistry.get(query)?.currentMatches ?? window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!isClient) return;
    const unsubscribe = subscribe(query, setMatches);
    // Seed the state from the shared registry so multiple consumers agree.
    const initial = mediaQueryRegistry.get(query)?.currentMatches;
    if (initial !== undefined) setMatches(initial);
    return unsubscribe;
  }, [query]);

  return matches;
};

export const useDocumentTitle = (title: string): void => {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Sweet Home Admin`;
    return () => {
      document.title = previous;
    };
  }, [title]);
};
